import Content from '../types/enums/content';
import DateUtils from './date';
import EpgEvent from '../classes/epgEvent';
import Event from '../classes/event';
import FastEvent from '../classes/fastEvent';
import MediaProduction from '../classes/mediaProduction';
import TranslationManager from '../assets/locale/translationsManager';
import { store } from '@shared/store/store';
import { IEventsState } from '../types/interfaces/states';
import {
  EventMap,
  IEPGEventRequest,
  IFastEventRequest,
  IMediaProductionEventRequest,
} from '../types/interfaces/event';

const EventUtils = {
  createEvent(
    request: IEPGEventRequest | IFastEventRequest | IMediaProductionEventRequest
  ) {
    switch (request.kind) {
      case Content.EPG_EVENT:
        return new EpgEvent(request as IEPGEventRequest);
      case Content.FAST_EVENT:
        return new FastEvent(request as IFastEventRequest);
      case Content.MEDIA_PRODUCTION:
        return new MediaProduction(request as IMediaProductionEventRequest);
    }
  },
  getCrew(crew: string, event?: Event | null) {
    if (!event) return '';

    let crewMembers = '';
    if (event?.crew && event?.crew[crew]?.length) {
      event.crew[crew].slice(0, 3).forEach((item) => {
        crewMembers += item + ', ';
      });
      return crewMembers.substring(0, crewMembers.length - 2);
    }

    return crewMembers;
  },
  getDate(event?: EpgEvent | null) {
    if (event) {
      const dayOfWeek = DateUtils.getTime({
        customDate: event.date?.start,
        displaySeconds: false,
        displayMinutes: false,
        displayHours: false,
        displayDayOfWeek: true,
      });
      const start = DateUtils.getTime({
        customDate: event.date?.start,
        displaySeconds: false,
      });
      const end = DateUtils.getTime({
        customDate: event.date?.end,
        displaySeconds: false,
      });
      return `${dayOfWeek}, ${TranslationManager.get(
        'MISC.EVENTS.BEGINNING'
      )}: ${start}h | ${TranslationManager.get('MISC.EVENTS.END')}: ${end}h`;
    }
    return '';
  },
  getInfo(event?: Event | null, limitLength = false) {
    if (!event) return '';
    const { categories } = store.getState();

    const year =
      event.year ||
      (event?.date?.start ? DateUtils.getYear(event.date.start) : null);
    const category = categories.data?.find(
      (item) => item.id === event?.categoryId
    )?.title;
    const genre = event.getGenreTitle && event.getGenreTitle();
    const categoryAndGenre = [category, genre]
      .filter((item) => !!item)
      .join(' / ');
    const countries = event.getCountries && event.getCountries();
    const duration =
      (event as EpgEvent | FastEvent).getDuration &&
      (event as EpgEvent | FastEvent).getDuration();
    const season = event.getSeasonNumber && event.getSeasonNumber();
    const episode = event.getEpisodeNumber && event.getEpisodeNumber();

    const data = [];
    if (year) data.push(year);
    if (categoryAndGenre) data.push(categoryAndGenre);
    if (countries) data.push(countries);
    // Do not push more info when countries length is > 40
    if (!limitLength || (limitLength && (countries?.length || 0) <= 40)) {
      if (duration) data.push(duration);
      if (season && episode) {
        data.push(`${season} ${episode}`);
      }
    }

    let info = '';
    for (let i = 0; i < data.length; i++) {
      info += data[i];
      if (i < data.length - 1) info += ' | ';
    }

    return info;
  },
  loadEvents(state: IEventsState, data: { events: EventMap; purge: boolean }) {
    state.eventsData = EventUtils.mergeEvents(state.eventsData, data.events);
    this.updateEvents(state);
    data.purge && this.purgeEvents(state);
  },
  loadMocks(state: IEventsState, events: EpgEvent[]) {
    // Generate and store mocked events
    state.mockedEvents = {};
    events.forEach((event: EpgEvent) => {
      state.mockedEvents[event.channelId] = event;
    });
  },
  mergeEvents(oldMap: EventMap, newMap: EventMap) {
    const mergedMap: EventMap = { ...oldMap };
    for (const [key, value] of Object.entries(newMap)) {
      if (mergedMap[key]) {
        const uniqueEvents = new Map(
          mergedMap[key].map((event) => [event.id, event])
        );
        newMap[key].forEach((event) => uniqueEvents.set(event.id, event));
        mergedMap[key] = Array.from(uniqueEvents.values());
      } else {
        mergedMap[key] = value;
      }
    }
    return mergedMap;
  },
  purgeEvents(state: IEventsState) {
    const now = Date.now();
    for (const [key] of Object.entries(state.eventsData)) {
      if (state.eventsData[key].length > 1) {
        state.eventsData[key] = state.eventsData[key].filter((item) => {
          if (item.date && item.date.end > now) {
            return item;
          }
        });
      }
    }
  },
  updateEvents(state: IEventsState) {
    const now = Date.now();
    for (const [key, events] of Object.entries(state.eventsData)) {
      // Look for the channel's current event
      state.currentEvents[key] = undefined;
      state.currentEvents[key] =
        events.find((event: EpgEvent) => {
          if (event.date && event.date?.start < now && event.date?.end > now) {
            return event;
          }
          return null;
        }) || state.currentEvents[key];
      // If no event found, set a mocked event
      if (!state.currentEvents[key]) {
        state.currentEvents[key] = state.mockedEvents[key];
      }
    }
  },
};

export default EventUtils;
